"use client";

import { Info } from "lucide-react";
import { Text } from "@/components/ui/text";
import {
  formatMomentumDisplay,
  momentumBadgeClass,
  momentumButtonClass,
} from "@/lib/momentum-badge";
import { cn } from "@/lib/utils";

interface MomentumBadgeProps {
  label: string | null | undefined;
  direction: string | null | undefined;
  onClick?: () => void;
  /** `soft` = tinted pill (tables/cards). `solid` = filled button (timeline). */
  variant?: "soft" | "solid";
  /** Defaults to `false`; `true` when `variant` is `solid`. */
  showInfoIcon?: boolean;
  className?: string;
}

const baseClass =
  "inline-flex shrink-0 items-center justify-center gap-1.5 border text-xs font-semibold leading-none transition-colors";

export function MomentumBadge({
  label,
  direction,
  onClick,
  variant = "soft",
  showInfoIcon,
  className,
}: MomentumBadgeProps) {
  const momentum = formatMomentumDisplay(label, direction);
  if (!momentum) return null;

  const isSolid = variant === "solid";
  const showIcon = showInfoIcon ?? isSolid;
  const colorClass = isSolid ? momentumButtonClass(label) : momentumBadgeClass(label);

  const styles = cn(
    baseClass,
    isSolid ? "h-8 rounded-lg px-3.5 shadow-sm transition-transform duration-200" : "rounded-full px-2.5 py-0.5",
    colorClass,
    onClick &&
      "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    onClick && isSolid && "hover:scale-110 active:scale-105",
    onClick && !isSolid && "hover:opacity-90",
    className,
  );

  const content = (
    <>
      {showIcon ? <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden /> : null}
      <Text className="text-inherit" size="xs" weight="semibold">{momentum.text }</Text>
    </>
  );

  if (!onClick) {
    return <span className={styles}>{content}</span>;
  }

  return (
    <button
      type="button"
      className={styles}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={`Deal momentum: ${momentum.text}. Open details.`}
    >
      {content}
    </button>
  );
}
